import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Beaker, MessageCircle, Sparkles } from "lucide-react";
import stickerStar from "@/assets/sticker-star.webp";

const storageKey = (userId: string) => `glowie_intro_seen_${userId}`;

export const GlowieIntroModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      const seen = localStorage.getItem(storageKey(user.id));
      if (!seen) setOpen(true);
    } catch {
      // ignore storage errors
    }
  }, [user]);

  const dismiss = () => {
    if (user) {
      try {
        localStorage.setItem(storageKey(user.id), "1");
      } catch {
        // ignore
      }
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent className="sm:max-w-lg rounded-3xl">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <img src={stickerStar} alt="" className="h-8 w-8" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Welcome to Glowie
            </span>
          </div>
          <DialogTitle className="font-display text-2xl">
            Meet Glowie, your skincare bestie
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            Glowie helps you understand your skin and build routines that
            actually work for it. Glowie is your skincare chemist, here to chat anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div className="flex gap-3 p-3 rounded-2xl bg-secondary/60">
            <MessageCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Chat with Glowie</div>
              <p className="text-sm text-muted-foreground">
                Ask about routines, ingredients, breakouts, or anything you're
                unsure about. She remembers your skin profile.
              </p>
            </div>
          </div>
          <div className="flex gap-3 p-3 rounded-2xl bg-secondary/60">
            <Beaker className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Get a custom formula</div>
              <p className="text-sm text-muted-foreground">
                Just say <em>"build me a formula for ___"</em> in chat. Glowie
                will craft a personalized routine with ingredients picked for
                your skin.
              </p>
            </div>
          </div>
          <div className="flex gap-3 p-3 rounded-2xl bg-secondary/60">
            <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Snap a skin analysis</div>
              <p className="text-sm text-muted-foreground">
                Upload a selfie any time for an AI read on your current skin
                state, plus tips tailored to what Glowie sees.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 sm:justify-between gap-2">
          <Button variant="ghost" onClick={dismiss}>
            Maybe later
          </Button>
          <Button asChild onClick={dismiss}>
            <Link to="/app/chat">Say hi to Glowie</Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};